import { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user role from Firestore by email
  const fetchUserRole = async (firebaseUser) => {
    if (!db || !auth) {
      setError('Firebase is not configured.');
      return null;
    }
    try {
      // Query users collection by email
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', firebaseUser.email)
      );
      const snapshot = await getDocs(usersQuery);

      if (!snapshot.empty) {
        const userDoc = snapshot.docs[0];
        const userData = userDoc.data();

        // Check if user is active (status field, not isActive)
        if (userData.status === 'inactive') {
          await signOut(auth);
          setError('Your account has been deactivated.');
          return null;
        }

        setUserRole(userData.role);
        return { ...userData, id: userDoc.id };
      } else {
        // User exists in Auth but not in Firestore (not an admin)
        await signOut(auth);
        setError('You do not have admin access.');
        return null;
      }
    } catch (err) {
      console.error('Error fetching user role:', err.code, err.message);
      if (err.code === 'permission-denied') {
        setError('Database permission error. Please contact admin.');
      } else {
        setError('Failed to verify admin access.');
      }
      return null;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    // If Firebase auth is not configured, just set loading to false
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userData = await fetchUserRole(firebaseUser);
        if (userData) {
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: userData.name || userData.displayName || firebaseUser.email,
            role: userData.role,
          });
        } else {
          setUser(null);
          setUserRole(null);
        }
      } else {
        setUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Login function
  const login = async (email, password) => {
    if (!auth) {
      setError('Firebase is not configured.');
      throw new Error('Firebase is not configured.');
    }
    setError(null);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const userData = await fetchUserRole(result.user);
      if (!userData) {
        throw new Error('Admin access denied');
      }
      return result.user;
    } catch (err) {
      console.error('Login error:', err.code, err.message);
      let message = 'Login failed. Please try again.';
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        message = 'Invalid email or password.';
      } else if (err.code === 'auth/wrong-password') {
        message = 'Incorrect password.';
      } else if (err.code === 'auth/invalid-email') {
        message = 'Invalid email address.';
      } else if (err.code === 'auth/too-many-requests') {
        message = 'Too many failed attempts. Please try again later.';
      } else if (err.message) {
        message = err.message;
      }
      setError(message);
      throw new Error(message);
    }
  };

  // Logout function
  const logout = async () => {
    if (!auth) return;
    try {
      await signOut(auth);
      setUser(null);
      setUserRole(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Reset password function
  const resetPassword = async (email) => {
    if (!auth) {
      throw new Error('Firebase is not configured.');
    }
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      console.error('Password reset error:', err.code, err.message);
      let message = 'Failed to send reset email. Please try again.';
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        message = 'No account found with this email address.';
      } else if (err.code === 'auth/invalid-email') {
        message = 'Invalid email address.';
      } else if (err.code === 'auth/too-many-requests') {
        message = 'Too many requests. Please try again later.';
      }
      throw new Error(message);
    }
  };

  // Check if user has specific permission
  const hasPermission = (resource, action) => {
    if (!userRole) return false;

    const permissions = {
      admin: {
        music: ['read', 'create', 'update', 'delete'],
        shows: ['read', 'create', 'update', 'delete'],
        merch: ['read', 'create', 'update', 'delete'],
        bookings: ['read', 'create', 'update', 'delete'],
        contacts: ['read', 'update', 'delete'],
        calendar: ['read', 'update'],
        users: ['read', 'create', 'update', 'delete'],
        settings: ['read', 'update'],
      },
      editor: {
        music: ['read', 'create', 'update'],
        shows: ['read', 'create', 'update'],
        merch: ['read', 'create', 'update'],
        bookings: ['read'],
        contacts: ['read'],
        calendar: ['read'],
        users: [],
        settings: ['read'],
      },
      booking_manager: {
        music: ['read'],
        shows: ['read'],
        merch: ['read'],
        bookings: ['read', 'create', 'update'],
        contacts: ['read', 'update'],
        calendar: ['read', 'update'],
        users: [],
        settings: ['read'],
      },
    };

    const rolePermissions = permissions[userRole];
    if (!rolePermissions) return false;

    const resourcePermissions = rolePermissions[resource];
    if (!resourcePermissions) return false;

    return resourcePermissions.includes(action);
  };

  const value = {
    user,
    userRole,
    loading,
    error,
    login,
    logout,
    resetPassword,
    hasPermission,
    isAdmin: userRole === 'admin',
    isEditor: userRole === 'editor' || userRole === 'admin',
    isBookingManager: userRole === 'booking_manager' || userRole === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
